import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import clsx from "clsx";
import type { ReactNode } from "react";
import styles from "./Tabs.module.css";

interface TabsRootProps<V extends string> {
  /** Keep the active tab in the URL (useSearchParams) so refresh and links restore it. */
  value?: V;
  defaultValue?: V;
  onValueChange?: (value: V) => void;
  children: ReactNode;
  className?: string;
}

function TabsRoot<V extends string>({ value, defaultValue, onValueChange, children, className }: TabsRootProps<V>) {
  return (
    <BaseTabs.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange ? (next) => onValueChange(next as V) : undefined}
      className={clsx(styles.root, className)}
    >
      {children}
    </BaseTabs.Root>
  );
}

function TabsList({ children, label, className }: { children: ReactNode; label: string; className?: string }) {
  return (
    <BaseTabs.List className={clsx(styles.list, className)} aria-label={label}>
      {children}
      <BaseTabs.Indicator className={styles.indicator} />
    </BaseTabs.List>
  );
}

function TabsTab({ value, children, disabled }: { value: string; children: ReactNode; disabled?: boolean }) {
  return (
    <BaseTabs.Tab value={value} disabled={disabled} className={styles.tab}>
      {children}
    </BaseTabs.Tab>
  );
}

function TabsPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <BaseTabs.Panel value={value} className={clsx(styles.panel, className)}>
      {children}
    </BaseTabs.Panel>
  );
}

/** Tabs (Base UI): <Tabs.Root value onValueChange><Tabs.List label><Tabs.Tab value/></Tabs.List><Tabs.Panel value/></Tabs.Root>. */
export const Tabs = { Root: TabsRoot, List: TabsList, Tab: TabsTab, Panel: TabsPanel };
